import { BranchTag } from "@/components/BranchTag";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { HealthBadge } from "@/components/HealthBadge";
import { PercentBar } from "@/components/PercentBar";
import { EmptyState } from "@/components/EmptyState";
import { CreateProjectDialog } from "@/pages/CreateProjectDialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { pct, fmtNum } from "@/lib/format";
const fmtCr = (v) => (v === null || v === undefined || Number(v) === 0 ? "—" : `₹ ${(Number(v) / 1e7).toLocaleString("en-IN", { maximumFractionDigits: 2 })} Cr`);
import { getErrorMessage } from "@/lib/errors";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Zap, AlertTriangle, ListChecks, Gauge, ShoppingCart, ClipboardList, Package, ChevronDown } from "lucide-react";

import { DelayRiskChart } from "@/components/DelayRiskChart";
const HEALTH_COLOR = { ON_TRACK: "#10B981", AT_RISK: "#F5B800", CRITICAL: "#EF4444" };
const SORT_OPTIONS = [
  { value: "risk", label: "Delay risk" },
  { value: "progress", label: "Overall progress" },
  { value: "variance", label: "Schedule variance" },
  { value: "issues", label: "Open issues" },
  { value: "name", label: "Name" },
  { value: "value", label: "Contract value" },
];

export default function Portfolio() {
  const { isOrgAdmin } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState("risk");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erpStats, setErpStats] = useState({ pendingPOs: 0, pendingIndents: 0, pendingGRNs: 0, openAlerts: 0 });

  // Section collapse states (all open by default)
  const [showApprovals, setShowApprovals] = useState(true);
  const [showChart,     setShowChart]     = useState(true);
  const [showTable,     setShowTable]     = useState(true);

  const loadErpStats = useCallback(async () => {
    try {
      const [posRes, indentsRes, grnsRes, alertsRes] = await Promise.allSettled([
        api.get("/procurement/purchase-orders"),
        api.get("/procurement/indents"),
        api.get("/procurement/grns"),
        api.get("/inventory/alerts"),
      ]);
      const pos = posRes.status === "fulfilled" ? posRes.value.data : [];
      const indents = indentsRes.status === "fulfilled" ? indentsRes.value.data : [];
      const grns = grnsRes.status === "fulfilled" ? grnsRes.value.data : [];
      const alerts = alertsRes.status === "fulfilled" ? alertsRes.value.data : [];
      setErpStats({
        pendingPOs: pos.filter((p) => p.status === "SUBMITTED").length,  // waiting for approval; approved ones are not pending
        pendingIndents: indents.filter((i) => i.status === "SUBMITTED").length,
        pendingGRNs: grns.filter((g) => g.status === "DRAFT").length,
        openAlerts: alerts.length,
      });
    } catch {
      /* non-blocking */
    }
  }, []);

  const load = useCallback(async (sort = sortBy) => {
    setLoading(true);
    try {
      const { data } = await api.get("/portfolio", { params: { sort_by: sort } });
      setOverview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [sortBy]);

  useEffect(() => { load(sortBy); }, [sortBy, load]);
  useEffect(() => { loadErpStats(); }, [loadErpStats]);

  const chartData = useMemo(
    () => (overview?.projects || []).map((p) => ({ code: p.code, risk_score: p.risk_score, health: p.health })),
    [overview]
  );

  const totals = overview?.totals;

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <PageHeader
        title="Portfolio Command Center"
        description={overview ? `${totals.projects} projects · as of ${overview.as_of}` : "Loading your organisation's projects…"}
        action={(isOrgAdmin || can("PROJECT_LIST", "create")) && <CreateProjectDialog onCreated={() => load()} />}
      />

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3" data-testid="portfolio-totals-cards">
        <StatCard icon={<Zap className="h-4 w-4" />} label="Contract value under tracking" value={fmtCr(totals?.total_value)} testId="stat-capacity" />
        <StatCard icon={<Gauge className="h-4 w-4" />} label="Value-weighted progress" value={pct(totals?.weighted_progress_pct ?? totals?.capacity_weighted_progress_pct)} testId="stat-progress" />
        <StatCard
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Health split"
          value={`${totals?.on_track ?? 0} / ${totals?.at_risk ?? 0} / ${totals?.critical ?? 0}`}
          hint="On track / At risk / Critical"
          testId="stat-health"
        />
        <StatCard icon={<ListChecks className="h-4 w-4" />} label="Open issues · alerts" value={`${totals?.open_issues ?? 0} · ${totals?.open_alerts ?? 0}`} testId="stat-issues" />
      </div>

      {/* ── Pending Approvals — collapsible ── */}
      <div className="rounded-xl border border-border bg-card" data-testid="erp-pending-approvals-section">
        <button
          type="button"
          onClick={() => setShowApprovals((v) => !v)}
          className="flex w-full items-center justify-between border-b border-border px-4 py-3 hover:bg-accent/30 transition-colors"
          data-testid="toggle-approvals"
        >
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Pending Approvals &amp; Actions</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">ERP items awaiting your review or action across all modules.</p>
          </div>
          <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", !showApprovals && "-rotate-90")} />
        </button>
        {showApprovals && (
          <div className="grid grid-cols-2 gap-0 sm:grid-cols-4">
            <ERPApprovalTile icon={<ShoppingCart className="h-5 w-5" />} label="Pending POs" count={erpStats.pendingPOs} href="/procurement/purchase-orders" color="blue" testId="erp-tile-pos" />
            <ERPApprovalTile icon={<ClipboardList className="h-5 w-5" />} label="Pending Indents" count={erpStats.pendingIndents} href="/procurement/purchase-indents" color="orange" testId="erp-tile-indents" />
            <ERPApprovalTile icon={<Package className="h-5 w-5" />} label="Open GRNs (Draft)" count={erpStats.pendingGRNs} href="/procurement/goods-receipt-notes" color="green" testId="erp-tile-grns" />
            <ERPApprovalTile icon={<AlertTriangle className="h-5 w-5" />} label="Stock Alerts" count={erpStats.openAlerts} href="/inventory/stock-balances" color="red" testId="erp-tile-alerts" />
          </div>
        )}
      </div>

      {/* ── Risk chart — collapsible ── */}
      {chartData.length > 0 && (
        <div className="rounded-xl border border-border bg-card" data-testid="portfolio-risk-chart-container">
          <button
            type="button"
            onClick={() => setShowChart((v) => !v)}
            className="flex w-full items-center justify-between border-b border-border px-4 py-3 hover:bg-accent/30 transition-colors"
            data-testid="toggle-chart"
          >
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Delay Risk by Project</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">Higher score = higher risk of schedule slippage.</p>
            </div>
            <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", !showChart && "-rotate-90")} />
          </button>
          {showChart && (
            <div className="p-4">
              <DelayRiskChart projects={overview?.projects || []} onOpen={(id) => navigate(`/projects/${id}`)} />
            </div>
          )}
        </div>
      )}

      {/* ── Projects table — collapsible + scrollable ── */}
      <div className="rounded-xl border border-border bg-card" data-testid="portfolio-projects-table-container">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <button
            type="button"
            onClick={() => setShowTable((v) => !v)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="toggle-table"
          >
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Projects ranked by</h2>
            <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", !showTable && "-rotate-90")} />
          </button>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-48" data-testid="portfolio-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`sort-option-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showTable && (
          !loading && (overview?.projects?.length ?? 0) === 0 ? (
            <EmptyState title="No projects yet" description="Create your first EPC project to start tracking progress." testId="portfolio-empty-state" />
          ) : (
            /* Fixed-height scrollable table body */
            <div className="max-h-[420px] overflow-y-auto" data-testid="portfolio-projects-scroll">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow>
                    <TableHead>Project</TableHead>
                    <TableHead>Health</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Variance</TableHead>
                    <TableHead>Contract value</TableHead>
                    <TableHead>Days left</TableHead>
                    <TableHead>Issues</TableHead>
                    <TableHead>Alerts</TableHead>
                    <TableHead>Risk</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(overview?.projects || []).map((p) => (
                    <TableRow
                      key={p.project_id}
                      data-testid={`portfolio-project-row-${p.project_id}`}
                      className="cursor-pointer"
                      onClick={() => navigate(`/projects/${p.project_id}`)}
                    >
                      <TableCell>
                        <p className="font-medium text-foreground">{p.name}</p>
                        <p className="font-mono text-xs text-muted-foreground">{p.code} · {p.location || "—"}<BranchTag row={p} /></p>
                      </TableCell>
                      <TableCell><HealthBadge health={p.health} reasons={p.health_reasons} showReasons testId={`health-badge-${p.project_id}`} /></TableCell>
                      <TableCell className="min-w-[140px]">
                        <div className="flex items-center gap-2">
                          <PercentBar actual={p.overall_actual_pct} planned={p.overall_planned_pct} testId={`percent-bar-${p.project_id}`} />
                          <span className="font-mono text-xs text-foreground">{pct(p.overall_actual_pct)}</span>
                        </div>
                      </TableCell>
                      <TableCell className={`font-mono text-xs ${p.variance_pct < 0 ? "text-rose-400" : "text-emerald-400"}`}>
                        {p.variance_pct >= 0 ? "+" : ""}{pct(p.variance_pct)}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-foreground">{fmtCr(p.contract_value)}</TableCell>
                      <TableCell className="font-mono text-xs text-foreground">{p.days_left ?? "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-foreground">{p.open_issues}</TableCell>
                      <TableCell className="font-mono text-xs text-foreground">{p.open_alerts} {p.critical_alerts > 0 && <span className="text-rose-400">({p.critical_alerts} crit)</span>}</TableCell>
                      <TableCell className="font-mono text-sm font-bold text-foreground">{fmtNum(p.risk_score, 1)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, hint, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <p className="text-xs font-semibold uppercase tracking-wider">{label}</p>
      </div>
      <p className="mt-2 font-mono text-2xl font-bold text-foreground">{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

const COLOR_MAP = {
  blue: "text-blue-500 bg-blue-500/10",
  orange: "text-orange-500 bg-orange-500/10",
  green: "text-emerald-500 bg-emerald-500/10",
  red: "text-rose-500 bg-rose-500/10",
};

function ERPApprovalTile({ icon, label, count, href, color, testId }) {
  const navigate = useNavigate();
  const colorCls = COLOR_MAP[color] || COLOR_MAP.blue;
  return (
    <button
      type="button"
      onClick={() => navigate(href)}
      data-testid={testId}
      className="group flex flex-col items-center gap-2 border-r border-border p-5 last:border-r-0 hover:bg-accent/50 transition-colors text-center"
    >
      <div className={`flex h-10 w-10 items-center justify-center rounded-full ${colorCls} transition-transform group-hover:scale-110`}>
        {icon}
      </div>
      <span className="text-2xl font-bold font-mono text-foreground">{count}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </button>
  );
}
